import { useState, useRef, useEffect } from 'react';
import { getWithExpiry } from '../utils/sessionStorage';
import { S3UploadMetadata } from '../types/file';

/*
  This hook is used to record audio and video from a media stream.
  per checking Safari, use mp4 format. - https://stackoverflow.com/a/73480671
  Firefox, use webm format.
  Other browsers, use webm or vp9 format.
*/
export const useRecording = () => {
  const fallBackGuid = getWithExpiry('applicantGuid');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const chunksRef = useRef<BlobPart[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [deviceRemovedDuringRecording, setDeviceRemovedDuringRecording] = useState(false);

  // for audio level checks
  const [audioLevel, setAudioLevel] = useState(0);
  const [audioRecordingError, setAudioRecordingError] = useState<string | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const AUDIO_LEVEL_THRESHOLD = 1; // strict
  const NO_AUDIO_THRESHOLD = 10; // seconds

  // audio refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const checkAudioLevel = (stream: MediaStream) => {
    setAudioRecordingError(null);
  
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
    }
    if (!analyserRef.current) {
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
    }
    const source = audioContextRef.current.createMediaStreamSource(stream);
    source.connect(analyserRef.current);
  
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
  
    const checkLevel = () => {
      if (!analyserRef.current) return;
  
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      setAudioLevel(average);
  
      if (average >= AUDIO_LEVEL_THRESHOLD) {
        // Audio detected: clear silence timer if running
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        setAudioRecordingError(null);
      } else {
        // No audio detected: start/restart silence timer
        if (!silenceTimeoutRef.current) {
          silenceTimeoutRef.current = setTimeout(() => {
            setAudioRecordingError("We can't hear you, please double check your audio devices.");
          }, NO_AUDIO_THRESHOLD * 1000);
        }
      }
  
      animationFrameRef.current = requestAnimationFrame(checkLevel);
    };
  
    checkLevel();
  };

  const stopAudioMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  
    // Clear the silence timer
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  };

  // Start recording with a given media stream
  const startRecording = (stream: MediaStream) => {
    try {
      setError(null);
      if (!stream.getAudioTracks().length) {
        console.warn('Stream does not contain audio tracks!');
      }

      chunksRef.current = [];

      // Browser detection (for optimal settings only, all should work)
      const isFirefox = navigator.userAgent.indexOf("Firefox") !== -1;
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                        /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      console.log('Browser detection:', { isFirefox, isSafari });

      // Universal approach that works across browsers
      const getMediaRecorderOptions = () => {
        // For Safari, it's better to let it choose its default
        if (isSafari) {
          try {
            // On newer Safari versions, we can try these formats
            if (MediaRecorder.isTypeSupported('video/mp4')) {
              return { mimeType: 'video/mp4' };
            }
            // Let Safari use its default
            console.log('Using Safari default format');
            return {};
          } catch (e) {
            console.log('Safari MediaRecorder error, using defaults:', e);
            return {};
          }
        }
        
        // For Firefox, simple formats work best
        if (isFirefox) {
          if (MediaRecorder.isTypeSupported('video/webm')) {
            return { mimeType: 'video/webm' };
          }
          if (MediaRecorder.isTypeSupported('audio/webm')) {
            return { mimeType: 'audio/webm' };
          }
        }
        
        // For other browsers (Chrome, Edge), try optimal formats
        const formats = [
          'video/webm;codecs=vp9',
          'video/webm;codecs=vp8',
          'video/webm',
          'video/mp4;codecs=h264',
          'video/mp4',
          'audio/webm',
          'audio/mp4',
        ];
        
        for (const format of formats) {
          if (MediaRecorder.isTypeSupported(format)) {
            console.log(`Using format: ${format}`);
            return { mimeType: format };
          }
        }
        
        // If no supported type found, let browser choose
        console.log('Using browser default format');
        return {};
      };

      const options = getMediaRecorderOptions();
      console.log('MediaRecorder options:', options);
      
      // Create the recorder with appropriate options
      const mediaRecorder = new MediaRecorder(stream, options);

      // Adjust timeslice based on browser
      const timeslice = isSafari ? 200 : 1000; // More frequent for Safari

      mediaRecorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
          console.log(`Added chunk: ${event.data.size} bytes`);
        }
      };

      mediaRecorder.onstop = () => {
        if (chunksRef.current.length === 0) {
          console.warn('No data chunks collected during recording');
          return;
        }
        
        // Determine the best blob type based on what we recorded with
        let blobOptions = {};
        if (options.mimeType) {
          blobOptions = { type: options.mimeType };
        } else if (isSafari) {
          // For Safari, if we didn't specify a mimeType, use mp4
          blobOptions = { type: 'video/mp4' };
        } else {
          // Default fallback
          blobOptions = { type: 'video/webm' };
        }
        
        console.log(`Creating blob with options:`, blobOptions);
        const blob = new Blob(chunksRef.current, blobOptions);
        console.log(`Created ${blob.size} byte blob`);
        
        const url = URL.createObjectURL(blob);
        setRecordedBlob(blob);
        setRecordedUrl(url);
        console.log('Recording complete, URL created');
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };

      // Start recording with appropriate timeslice
      mediaRecorder.start(timeslice);
      
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      console.log(`Recording started with options:`, options);
    } catch (error) {
      console.error('Recording initialization error:', error);
      setError(`Recording failed. Please check your camera and microphone permissions. (${Date.now()})`);
      // Try a fallback approach for Safari without specifying mimeType
      try {
        console.log('Trying fallback recording method...');
        const mediaRecorder = new MediaRecorder(stream);
        
        mediaRecorder.ondataavailable = event => {
          if (event.data && event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };
        
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunksRef.current);
          const url = URL.createObjectURL(blob);
          setRecordedBlob(blob);
          setRecordedUrl(url);
          console.log('Fallback recording complete');
        };
        
        mediaRecorder.start(200); // Short timeslice for compatibility
        mediaRecorderRef.current = mediaRecorder;
        setIsRecording(true);
        console.log('Fallback recording started');
      } catch (fallbackError) {
        console.error('Fallback recording also failed:', fallbackError);
        setError(`Recording failed. Please check your camera and microphone permissions. (${Date.now()})`);
      }
    }
  };

  const monitorAudioDevices = async () => {
    try {
      setDeviceRemovedDuringRecording(true);
      setError(`Your microphone device may have been disconnected. Please reconnect your microphone and try again. (${Date.now()})`);
      setRecordingError(`Your microphone device may have been disconnected. Please reconnect your microphone and try again. (${Date.now()})`);
      console.error(`Your microphone device may have been disconnected. Please reconnect your microphone and try again. (${Date.now()})`);
    } catch (err) {
      console.error('Error monitoring audio devices:', err);
    }
  };
  
  // Add this effect to continuously monitor audio devices
  useEffect(() => {
    // Initial check
    // monitorAudioDevices();
  
    // Set up device change listener
    const handleDeviceChange = () => {
      console.error('Device change detected, checking audio devices...');
      monitorAudioDevices();

    };
  
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
  
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  const getVideoRecorderOptions = () => {
    const isFirefox = navigator.userAgent.indexOf("Firefox") !== -1;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                    /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Safari: prefer mp4
    if (isSafari) {
      if (MediaRecorder.isTypeSupported('video/mp4')) {
        return { mimeType: 'video/mp4' };
      }
      return {}; // Default
    }
    
    // Firefox: prefer webm
    if (isFirefox) {
      if (MediaRecorder.isTypeSupported('video/webm')) {
        return { mimeType: 'video/webm' };
      }
    }
    
    // Other browsers: try various formats
    const formats = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4'
    ];
    
    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        return { mimeType: format };
      }
    }
    
    return {}; // Default fallback
  };

  // for ws streaming
  const startStreamedRecording = async (
    applicantGuid: string = fallBackGuid, 
    questionIndex: number, 
    questionText: string, 
    isLastQuestion: boolean = false, 
    totalQuestions: number,
    s3MetaData: S3UploadMetadata
  ) => {
    try {
      setError(null);
      setDeviceRemovedDuringRecording(false);
      const videoDeviceId = getWithExpiry('selectedCameraDeviceId');
      const audioDeviceId = getWithExpiry('selectedAudioDeviceId');
      
      // Reset state
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // setIsRecording(false);
      setRecordingError(null);
      setProgress(0);
      const isAndroid = /android/i.test(navigator.userAgent);

      // Create constraints with persisted devices if they exist
      const constraints: MediaStreamConstraints = {
        video: isAndroid ? 
        {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        } : videoDeviceId ? 
          { 
            deviceId: { exact: videoDeviceId },
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { min: 30, ideal: 30 }
          } : 
          {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { min: 30, ideal: 30 }
          },
        audio: audioDeviceId ? 
          { 
            deviceId: { exact: audioDeviceId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } : 
          {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
      };

      console.log('Using persisted devices for recording:', { 
        video: videoDeviceId || 'default', 
        audio: audioDeviceId || 'default' 
      });
    
      // Get stream with the specific devices
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      await checkAudioLevel(stream);

      // Monitor for device removal during recording
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();

      const handleTrackEnded = (event: Event) => {
        const track = event.target as MediaStreamTrack;
        console.log('Media track ended during recording:', track.kind, track.readyState);
        
        if (track.readyState === 'ended') {
          setDeviceRemovedDuringRecording(true);
          const deviceType = track.kind === 'video' ? 'camera' : 'microphone';
          setError(`Your ${deviceType} device has been disconnected during recording. Please reconnect your device and try again. (${Date.now()})`);
          setRecordingError(`Your ${deviceType} device has been disconnected during recording. Please reconnect your device and try again. (${Date.now()})`);
          console.error(`Your ${deviceType} device has been disconnected during recording. Please reconnect your device and try again. (${Date.now()})`);

          // Stop recording if device was removed
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          
          // Close WebSocket connection
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.close();
          }
          
          setIsRecording(false);
        }
      };
      
      videoTracks.forEach(track => {
        track.addEventListener('ended', handleTrackEnded);
      });
      audioTracks.forEach(track => {
        track.addEventListener('ended', handleTrackEnded);
      });
      // Create MediaRecorder with proper options
      const options = await getVideoRecorderOptions();
      console.log('Using MediaRecorder options:', options);
      const recorder = await new MediaRecorder(stream, options);

      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;

      const cleanupTrackListeners = () => {
        videoTracks.forEach(track => {
          track.removeEventListener('ended', handleTrackEnded);
        });
        audioTracks.forEach(track => {
          track.removeEventListener('ended', handleTrackEnded);
        });
      };
      
      // Set up WebSocket connection
      const socketUrl = getWebSocketUrl();
      console.log('Connecting to WebSocket:', socketUrl);
      const ws = await new WebSocket(socketUrl);
      wsRef.current = ws;
      // Track socket state
      let socketConnected = false;
      let recordingStarted = false;

      // Create array to store chunks locally
      const chunks: BlobPart[] = [];
      
      // Handle WebSocket connection open
      ws.onopen = () => {
        console.log('WebSocket connection established');
        socketConnected = true;

        // Send metadata first before recording starts
        ws.send(JSON.stringify({
          type: 'metadata',
          applicantGuid,
          questionId: questionIndex,
          questionText,
          isLastQuestion,
          totalQuestions,
          s3MetaData
        }));
        
        // Only start recording after socket is connected
        setIsRecording(true);
        if (!recordingStarted) {
          recorder.start(1000); // Send data every 1 second
          recordingStarted = true;
          console.log('MediaRecorder started');
        }
      };
      
      // Handle WebSocket errors
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setRecordingError('Connection error. Please try again.');
        
        // Clean up
        if (recorder.state === 'recording') recorder.stop();
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };
      
      // Handle WebSocket closure
      ws.onclose = () => {
        console.log('WebSocket connection closed 1');
        socketConnected = false;
      };

      // Stream data chunks as they become available
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          // Store chunk locally for replay purposes
          chunks.push(event.data);

          // Send chunk to server if socket is connected
          if(socketConnected){
            console.log(`Sending chunk: ${Math.round(event.data.size / 1024)} KB`);
            ws.send(event.data);
          }
        }
      };
      
      // Handle recording stop
      recorder.onstop = () => {
        console.log('MediaRecorder stopped');
        console.log('WebSocket readyState:', ws.readyState, 
          ws.readyState === 0 ? 'CONNECTING' :
          ws.readyState === 1 ? 'OPEN' :
          ws.readyState === 2 ? 'CLOSING' :
          ws.readyState === 3 ? 'CLOSED' : 'UNKNOWN');

        const mimeType = options.mimeType || 'video/webm';
        const videoBlob = new Blob(chunks, { type: mimeType });
        const videoUrl = URL.createObjectURL(videoBlob);

        // Save the local recording reference
        setRecordedBlob(videoBlob);
        setRecordedUrl(videoUrl);
        
        // Generate thumbnail from the local recording
        generateThumbnail(videoBlob, questionIndex);
        
        // Close WebSocket if still open
        // if (socketConnected && ws.readyState === WebSocket.OPEN) {
        //   ws.close();
        // }

        ws.onmessage = (event) => {
          // console.log('Raw WebSocket event:', event);
          console.log('WebSocket message data:', event.data);
          
          try {
            // Parse the string data into JSON
            const parsedData = JSON.parse(event.data);
            // console.log('Parsed message data:', parsedData);
            
            if (parsedData.type === 'upload_complete') {
              
              // Set the URLs in your state
              setVideoUrl(parsedData.videoUrl);
              setAudioUrl(parsedData.audioUrl);
              setTranscription(parsedData.transcription);

              // Update progress
              setProgress(100);
            } else if (parsedData.type === 'error') {
              console.error('Server error:', parsedData.message);
              setRecordingError(parsedData.message);
            } else if (parsedData.type === 'pong') {
              console.log('Received pong - connection alive');
            } else if (parsedData.type === 'metadata_received') {
              console.log('Server received metadata');
            } else if (parsedData.type === 'answer_submitted') {
              console.log('Answer submitted to API', parsedData.isReconciled);
            }

          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
            console.log('Raw message content:', event.data);
          }
        };
        
        // Clean up media stream
        stream.getTracks().forEach(track => track.stop());
        // setIsRecording(false);

      };
      
      // Handle recorder errors
      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setRecordingError('Recording failed. Please try again.');
        
        // Clean up
        if (socketConnected && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
        
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };
      
      // Store recorder and stream for later access
      mediaRecorderRef.current = recorder;
      mediaStreamRef.current = stream;
      
      // If socket connected before recorder was ready
      if (socketConnected && !recordingStarted) {
        recorder.start(1000);
        recordingStarted = true;
        setIsRecording(true);
        console.log('MediaRecorder started');
      }
      
      return () => {
        // Cleanup function
        cleanupTrackListeners();
        if (recorder.state === 'recording') recorder.stop();
        if (socketConnected && ws.readyState === WebSocket.OPEN) ws.close();
        stream.getTracks().forEach(track => track.stop());
      };
    } catch (error) {
      console.error('Failed to start streaming recording:', error);
      setRecordingError((error as Error).message || 'Failed to access camera/microphone');
      setError(`Recording failed. Please check your camera and microphone permissions. (${Date.now()})`);
      return null;
    }
  };

  // for ws stop streaming
  const stopStreamedRecording = () => {
    stopAudioMonitoring();
    console.log('stopStreamedRecording: Stopping streamed recording');
    
    // Stop MediaRecorder if it's recording
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    // Use wsRef to access the WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("User has stopped recording");
    }
    setIsRecording(false);
  };

  const handleSubmitStreamRecording = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Now send the signal to actually process the video
      wsRef.current.send(JSON.stringify({
        type: 'process_recording',
      }));
      
      console.log('Sent process_recording signal to server');
    } else {
      console.error('Cannot submit recording: WebSocket is not connected');
    }
  };

  const getWebSocketUrl = () => {
    // Check if we're in development (localhost)
    const isDev = 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1';
    
    if (isDev) {
      // Development: Use port 3001 (assuming your WebSocket server runs on 3001)
      return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.hostname}:3001/stream`;
    } else {
      // Production: Use same host without port manipulation
      // WebSockets will be proxied at the infrastructure level
      return `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/stream`;
    }
  };

  const generateThumbnail = async (videoBlob: Blob, questionIndex: number) => {
    const videoUrl = URL.createObjectURL(videoBlob);
    const video = document.createElement('video');
    video.src = videoUrl;
    video.muted = true;
    
    // Return a promise that resolves when the thumbnail is generated
    return new Promise<void>((resolve) => {
      video.onloadeddata = () => {
        // Seek to 1 second or halfway for short videos
        video.currentTime = Math.min(1, video.duration / 2);
      };
      
      video.onseeked = () => {
        // Create canvas to capture the frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw the video frame to canvas
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Create thumbnail URL
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
          const questionId = `question_${questionIndex}`;
          
          // Save thumbnail
          setThumbnails(prev => ({ ...prev, [questionId]: thumbnailUrl }));
          
          // Clean up
          URL.revokeObjectURL(videoUrl);
          resolve();
        }
      };
      
      // Handle errors
      video.onerror = () => {
        URL.revokeObjectURL(videoUrl);
        resolve(); // Resolve without thumbnail
      };
    });
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        console.log('Recording stopping...');
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    }
  };

  const clearRecording = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
  };

  const setRecordedData = (blob: Blob, url: string) => {
    setRecordedBlob(blob);
    setRecordedUrl(url);
  };

  return {
    isRecording,
    recordedBlob,
    recordedUrl,
    startRecording,
    setIsRecording,
    stopRecording,
    clearRecording,
    setRecordedData,
    startStreamedRecording,
    stopStreamedRecording,
    videoUrl,
    audioUrl,
    transcription,
    handleSubmitStreamRecording,
    error,
    deviceRemovedDuringRecording,
    setDeviceRemovedDuringRecording,
    setError,
    audioRecordingError
  };
};

export default useRecording;